import {
  CurrencyEnum,
  FieldErrorAndDescription,
  FormSelect,
  Hint,
  PaymentMethodEnum,
} from '@systemeio/ui'
import { usePaymentMethods } from 'modules/payment-methods/hooks/use-payment-methods'
import { useEffect } from 'react'
import { EditableItemType } from 'shared/components/edit-modal'
import { useCurrencyData } from 'shared/hooks/use-currency-data'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import PaymentMethods from '../../modules/payment-methods/components/payment-methods'
import { useCurrency } from '../../modules/payment-methods/hooks/use-currency'
import useUser from './use-user'

export const usePaymentMethodsByCurrency = <
  T extends {
    currency?: CurrencyEnum
    paymentMethods?: PaymentMethodEnum[]
  },
>({
  currency,
  errors,
  currencyChangeMessage,
  allowedPaymentMethods = Object.values(PaymentMethodEnum),
}: {
  currency?: CurrencyEnum
  errors: {
    currency: string
    paymentMethods: string
  }
  currencyChangeMessage?: string
  allowedPaymentMethods?: PaymentMethodEnum[]
}) => {
  const { isRootAdmin } = useUser()
  const { t } = useLocoTranslation()

  const currencyData = useCurrencyData()

  currencyChangeMessage =
    currencyChangeMessage ?? (t('dashboard.payment_methods.currency.change_info') as string)

  const { currentCurrency, setCurrentCurrency, showToast } = useCurrency({ currencyChangeMessage })
  const { data: paymentMethodsData } = usePaymentMethods(currentCurrency)

  useEffect(() => {
    setCurrentCurrency(currency)
  }, [currency, setCurrentCurrency])

  const currencyAndPaymentMethodsEditFields: EditableItemType<T> = [
    {
      field: 'currency',
      onRender: (value, _, state, __, isPreFetching, onChangeState) => (
        <FormSelect
          withoutCloseIcon
          key="currency-edit"
          placeholder={t('global.currency')}
          label={<Hint label={currencyChangeMessage ?? ''}>{t('global.currency')}</Hint>}
          data={currencyData}
          value={value as T['currency']}
          onChange={data => {
            onChangeState({
              ...state,
              currency: data,
              paymentMethods: [],
            })
            setCurrentCurrency(data)
            showToast()
          }}
          isPreFetching={isPreFetching}
          error={errors.currency}
        />
      ),
    },
    {
      field: 'paymentMethods',
      onRender: (value, onChange, _, __, isPreFetching) => (
        <div className="flex flex-col gap-2">
          <PaymentMethods
            isPreFetching={isPreFetching}
            key={'payment-methods-edit'}
            data={paymentMethodsData?.paymentMethods.filter(pm => {
              if (pm === PaymentMethodEnum.paypal_rest && isRootAdmin) {
                return false
              }

              return allowedPaymentMethods.includes(pm)
            })}
            value={(value ?? []) as T['paymentMethods']}
            onChange={onChange as (value: T['paymentMethods']) => void}
          />
          {errors && errors.paymentMethods && (
            <FieldErrorAndDescription error={errors.paymentMethods} />
          )}
        </div>
      ),
      shouldRender: state => !!state.currency || !!errors.paymentMethods,
    },
  ] as const

  return {
    currencyAndPaymentMethodsEditFields,
  }
}
